import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SnackbarStatus, useSnackbar } from '@weareopenr/component-library';
import { TEditVenueOpeningTimes } from '../../components/VenueOpeningTimeEditModal/types';
import { FieldNamesMarkedBoolean } from 'react-hook-form';
import { deleteVenueOpeningTime } from '../../api/deleteVenueOpeningTime';
import { createVenueOpeningTimesPromise } from './createVenueOpeningTimesPromise/createVenueOpeningTimesPromise';
import { updateExistingOpeningTimes } from './updateExistingOpeningTimes/updateExistingOpeningTimes';
import { validateResponses } from './validateResponses/validateResponses';

interface IFormSubmission {
  data: TEditVenueOpeningTimes;
  dirtyFields: Partial<Readonly<FieldNamesMarkedBoolean<TEditVenueOpeningTimes>>>;
  deletedOpeningTimes: string[];
}

export const useUpdateVenueOpeningTimes = (venueId: string, onMutationSuccess: () => void) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ data, dirtyFields, deletedOpeningTimes }: IFormSubmission): Promise<void> => {
      const deleteResponses = await Promise.all([
        ...deletedOpeningTimes.map((id: string) => deleteVenueOpeningTime(venueId, id)),
      ]);
      validateResponses(deleteResponses);
      const updateResponses = await Promise.all([...updateExistingOpeningTimes(venueId, data, dirtyFields)]);
      validateResponses(updateResponses);
      const createResponses = await Promise.all([...createVenueOpeningTimesPromise(venueId, data)]);
      validateResponses(createResponses);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['venue'] });
      showSnackbar({
        message: 'You have successfully updated opening times.',
        status: SnackbarStatus.SUCCESS,
      });
      onMutationSuccess();
    },
    onError: () => {
      showSnackbar({
        message: 'Something went wrong whilst updating venue opening times.',
        status: SnackbarStatus.ERROR,
      });
    },
  });
};
